import React from "react";
import { ArrowRight, CalendarDays, FlaskConical, MessageCircle, Pill, ShieldCheck, Star, Stethoscope } from "lucide-react";
import Tilt from "./template/view.tilt";

export const PLAY_URL = "https://play.google.com/store/apps/details?id=com.wendyam23.medichelp";

/** Small HTML rendition of the app's home screen, inside a 3D phone. */
function PhoneMockup() {
    const tiles = [
        { icon: Stethoscope, label: "Médecins", color: "#6A52ED" },
        { icon: Pill, label: "Pharmacies", color: "#0EA5A4" },
        { icon: FlaskConical, label: "Labos", color: "#F59E0B" },
        { icon: MessageCircle, label: "Messages", color: "#EC4899" },
    ];
    return (
        <div className="relative mx-auto w-[270px] sm:w-[300px]" style={{ perspective: 1400 }}>
            <div className="phone-float" style={{ transformStyle: "preserve-3d" }}>
                <div className="relative rounded-[44px] bg-[#0d0b24] p-3 shadow-[0_60px_120px_-30px_rgba(10,5,50,0.75),0_0_0_2px_rgba(255,255,255,0.08)_inset]">
                    <div className="absolute left-1/2 top-4 -translate-x-1/2 w-24 h-6 rounded-full bg-black z-10" />
                    <div className="rounded-[34px] bg-[#f7f6fd] overflow-hidden h-[540px] sm:h-[600px] px-4 pt-12">
                        <p className="text-[11px] text-slate-400 font-medium">Lundi 3 novembre</p>
                        <p className="text-lg font-extrabold text-slate-900 -mt-0.5">Bonjour, Awa</p>
                        <div className="mt-3 h-9 rounded-xl bg-white border border-slate-100 flex items-center px-3 text-[11px] text-slate-400">Rechercher un symptôme…</div>
                        <div className="mt-4 rounded-2xl p-4 text-white bg-gradient-to-br from-brand-500 via-brand-700 to-brand-900 relative overflow-hidden" style={{ transform: "translateZ(30px)" }}>
                            <div className="absolute -top-10 -right-8 w-32 h-32 rounded-full bg-white/10" />
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-white/15 rounded-full px-2 py-0.5"><Star className="w-3 h-3 text-amber-300" fill="currentColor" /> Essai Premium · 90 j</span>
                            <p className="mt-2 text-base font-extrabold leading-tight">Votre santé,<br />à portée de main</p>
                            <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold bg-white text-brand-600 rounded-lg px-2.5 py-1.5">Trouver un médecin <ArrowRight className="w-3 h-3" /></span>
                        </div>
                        <p className="mt-4 text-[10px] font-bold tracking-wider text-slate-400">SERVICES</p>
                        <div className="mt-2 grid grid-cols-4 gap-2">
                            {tiles.map(({ icon: Icon, label, color }) => (
                                <div key={label} className="rounded-xl bg-white border border-slate-100 py-2 flex flex-col items-center gap-1 shadow-sm">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}1f`, color }}><Icon className="w-3.5 h-3.5" /></div>
                                    <span className="text-[8.5px] font-semibold text-slate-600">{label}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-[10px] font-bold tracking-wider text-slate-400">RDV À VENIR</p>
                        <div className="mt-2 rounded-xl bg-white border border-slate-100 p-3 flex items-center gap-3 shadow-sm">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-200 to-rose-300" />
                            <div className="flex-1">
                                <p className="text-[11px] font-bold text-slate-800">Dr. Kaboré · Pédiatre</p>
                                <p className="text-[10px] text-slate-400">Mar. 4 nov · 09:30 · En ligne</p>
                            </div>
                            <CalendarDays className="w-4 h-4 text-brand-500" />
                        </div>
                    </div>
                </div>
            </div>
            {/* floating chips */}
            <div className="hidden sm:flex absolute -left-16 top-24 items-center gap-2 bg-white rounded-2xl px-3 py-2 shadow-xl text-xs font-bold text-slate-700">
                <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></span>
                Médecins vérifiés
            </div>
            <div className="hidden sm:flex absolute -right-12 bottom-28 items-center gap-2 bg-white rounded-2xl px-3 py-2 shadow-xl text-xs font-bold text-slate-700">
                <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Pill className="w-4 h-4" /></span>
                Pharmacies de garde
            </div>
        </div>
    );
}

export function StoreBadges({ dark = true }) {
    const base = `flex items-center gap-2.5 px-5 py-2.5 rounded-xl transition-transform ${dark ? "bg-black text-white" : "bg-white text-slate-900 border border-slate-200"}`;
    return (
        <div className="flex flex-wrap gap-3 items-center">
            <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" className={`${base} hover:-translate-y-0.5`}>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <span className="text-left leading-tight"><span className="block text-[10px] opacity-70">Disponible sur</span><span className="block text-sm font-bold">Google Play</span></span>
            </a>
            {/* The iOS app is not published yet: no dead link, just an honest badge. */}
            <div className={`${base} opacity-70 cursor-default`} aria-label="Bientôt disponible sur l'App Store">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span className="text-left leading-tight"><span className="block text-[10px] opacity-70">Bientôt sur</span><span className="block text-sm font-bold">App Store</span></span>
            </div>
        </div>
    );
}

export default function ViewHero() {
    return (
        <section id="top" className="relative overflow-hidden bg-gradient-to-br from-brand-500 via-brand-700 to-brand-950 text-white pt-28 pb-24 lg:pt-36 lg:pb-32">
            <div className="absolute inset-0 grain opacity-60" />
            <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-fuchsia-400/20 blur-3xl" />
            <div className="absolute -bottom-48 -left-40 w-[32rem] h-[32rem] rounded-full bg-sky-400/10 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <span className="inline-flex items-center gap-2 text-xs font-bold tracking-wide bg-white/10 border border-white/15 rounded-full px-3 py-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> Disponible au Burkina Faso
                    </span>
                    <h1 className="mt-6 text-[2.6rem] sm:text-6xl font-extrabold leading-[1.05] tracking-tight">
                        Votre santé,<br /><span className="text-brand-200">à portée de main.</span>
                    </h1>
                    <p className="mt-6 text-lg text-white/75 max-w-xl">
                        Prenez rendez-vous avec un médecin, consultez en vidéo, trouvez une pharmacie de garde et gardez vos ordonnances au même endroit.
                    </p>
                    <div className="mt-8 grid gap-3 sm:flex sm:flex-wrap">
                        <a href="#download" className="inline-flex justify-center items-center gap-2 bg-white text-brand-700 px-6 py-3.5 rounded-full font-bold shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition-transform">
                            Essayer gratuitement <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href="#join" className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full font-bold border border-white/25 hover:bg-white/10 transition-colors">
                            Je suis médecin
                        </a>
                    </div>
                    <p className="mt-5 text-sm text-white/60">3 mois de Premium offerts à l'inscription. Sans carte bancaire.</p>
                </div>

                <Tilt max={6} className="pt-4">
                    <PhoneMockup />
                </Tilt>
            </div>
        </section>
    );
}
